// Modelo aerodinámico precalculado para la simulación. Ver docs/theory/barrowman.md y simulation.md.
import { attachedFront, bodyRadiusAt, layoutStack, noseRadius, profileVolume, referenceDiameter } from '../rocket/geometry';
import type { FinSet, Rocket } from '../rocket/types';
import { finCenterOfPressure, finCnAlpha } from './barrowman';

export interface AeroSums {
  /** Σ CNα. */
  readonly a0: number;
  /** Σ CNα·x. */
  readonly a1: number;
  /** Σ CNα·x². */
  readonly a2: number;
}

export interface AeroModel {
  readonly refDiameter: number;
  readonly refArea: number;
  /** Sumas de las contribuciones de fuerza normal para un Mach dado. */
  sums(mach: number): AeroSums;
}

interface Fixed {
  readonly cnAlpha: number;
  readonly x: number;
}
interface FinEntry {
  readonly fin: FinSet;
  readonly bodyRadius: number;
  readonly x: number;
}

export function createAeroModel(rocket: Rocket): AeroModel {
  const d = referenceDiameter(rocket);
  const refRadius = d / 2;
  const fixed: Fixed[] = [];
  const finEntries: FinEntry[] = [];

  for (const entry of layoutStack(rocket)) {
    const c = entry.component;
    if (c.type === 'nose-cone') {
      const rn = c.baseDiameter / 2;
      const volume = profileVolume((x) => noseRadius(c, x), c.length);
      fixed.push({ cnAlpha: 2 * (rn / refRadius) ** 2, x: entry.x0 + c.length - volume / (Math.PI * rn * rn) });
      continue;
    }
    if (c.type === 'transition' && c.foreDiameter !== c.aftDiameter) {
      const rf = c.foreDiameter / 2;
      const ra = c.aftDiameter / 2;
      fixed.push({ cnAlpha: 2 * ((ra / refRadius) ** 2 - (rf / refRadius) ** 2), x: entry.x0 + (c.length / 3) * (1 + 1 / (1 + rf / ra)) });
    }
    for (const child of c.children) {
      if (child.type !== 'fin-set') continue;
      const front = attachedFront(entry, child.rootChord, child.position);
      finEntries.push({ fin: child, bodyRadius: bodyRadiusAt(entry, front + child.rootChord / 2), x: front + finCenterOfPressure(child) });
    }
  }

  const fixedSums = fixed.reduce((s, f) => ({ a0: s.a0 + f.cnAlpha, a1: s.a1 + f.cnAlpha * f.x, a2: s.a2 + f.cnAlpha * f.x * f.x }), { a0: 0, a1: 0, a2: 0 });

  return {
    refDiameter: d,
    refArea: (Math.PI * d * d) / 4,
    sums(mach: number): AeroSums {
      let { a0, a1, a2 } = fixedSums;
      for (const e of finEntries) {
        const cn = finCnAlpha(e.fin, d, e.bodyRadius, mach);
        a0 += cn;
        a1 += cn * e.x;
        a2 += cn * e.x * e.x;
      }
      return { a0, a1, a2 };
    },
  };
}

/** Σ CNα·(x − x_cg)², que gobierna la amortiguación de cabeceo. */
export const dampingSum = (s: AeroSums, cgX: number): number => s.a2 - 2 * cgX * s.a1 + cgX * cgX * s.a0;
